<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'contract',
    path: '/contracts/:id',
    component: 'Contract',
    params: [
      {
        name: 'id',
        prop: 'contractId',
      },
      {
        name: 'rev',
        prop: 'revision_number',
      },
    ],
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'Contract',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    contractId: {
      type: String,
      required: false,
    },
    revision_number: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const tabManager = ref(null);
  const item = ref(null);
  const ux = ref({
    initialLoadSpinner: true,
  });
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_8564662037462133_parentTo = computed(() => ({
    name: 'app.contracts',
  }));

  const $data = computed(() => ({
    tabManager: tabManager.value,
    item: item.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_8564662037462133_parentTo: b_8564662037462133_parentTo.value,
  }));

  const $$load_tabManager = () => {
    return new $app.value.utils.LazyTabManager();
  };
  const $$load_item = () => {
    return $fn.value.fetch('read_contract', {
      asResource: true,
      parameters: { id: props.contractId, rev: props.revision_number },
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'tabManager' },
      { name: 'item' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_8564662037462133_parentTo', nodeId: '8564662037462133' },
    ],
    loaders: { tabManager: $$load_tabManager, item: $$load_item },
    ux: { initialLoadSpinner: true },
    dataRefs: {
      tabManager,
      item,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_8564662037462133_parentTo,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const readonly = computed(() => {
    if (loaded.value) {
      return item.value.locked;
    }
    return null;
  });
  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: [
          item.value.snapshot('contract_number'),
          $fn.value.gettext('Contract'),
        ],
        icon: $config.value.contract.icon,
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.contractId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('contractId');
    }
  );
  watch(
    () => props.revision_number,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('revision_number');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_hrgt48ii5_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };

  const act_7315092382398562_closeModal_hrgt48ii5 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_hrgt48ii5_getActionArgs,
    });
  const inst_evh_7315092382398562_close_45ku0wt3m =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_hrgt48ii5,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_45ku0wt3m],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_s2es1098e =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_tbmkzars7 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_s2es1098e,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_tbmkzars7],
    event: 'reload',
  });
  const act_4615685607825208_reloadComponentData_skpexhf82 =
    new actionTypes.ReloadComponentDataAction({
      displayName: 'reload',
    });
  const inst_evh_4615685607825208_clickPreventStop_7xdcyzqln =
    new actionTypes.EventHandlerImpl({
      action: act_4615685607825208_reloadComponentData_skpexhf82,
      event: 'click.prevent.stop',
      displayName: 'reload',
    });
  const inst_evh_4615685607825208_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_4615685607825208_clickPreventStop_7xdcyzqln],
      event: 'click.prevent.stop',
    });
  const act_2095089031469157_closeComponent_hqo61413e =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2095089031469157_success_vkocbsbe3 =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_closeComponent_hqo61413e,
      event: 'success',
      displayName: 'closeComponent',
    });
  const inst_evh_2095089031469157_success = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2095089031469157_success_vkocbsbe3],
    event: 'success',
  });
  const act_2679161326024595_reloadComponentData_06w3sl6yy =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_2679161326024595_reload_wfmu2365x =
    new actionTypes.EventHandlerImpl({
      action: act_2679161326024595_reloadComponentData_06w3sl6yy,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_2679161326024595_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2679161326024595_reload_wfmu2365x],
    event: 'reload',
  });
  const act_7987652045662005_reloadComponentData_od6446bb4 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7987652045662005_reload_0o3l7dbxa =
    new actionTypes.EventHandlerImpl({
      action: act_7987652045662005_reloadComponentData_od6446bb4,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7987652045662005_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7987652045662005_reload_0o3l7dbxa],
    event: 'reload',
  });
  const act_6490121676898844_reloadComponentData_fgvgkl2it =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_6490121676898844_reload_44387hp8c =
    new actionTypes.EventHandlerImpl({
      action: act_6490121676898844_reloadComponentData_fgvgkl2it,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_6490121676898844_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_6490121676898844_reload_44387hp8c],
    event: 'reload',
  });
  const act_2789477278097085_reloadComponentData_1j5o71zmj =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_2789477278097085_reload_qn9ec8i16 =
    new actionTypes.EventHandlerImpl({
      action: act_2789477278097085_reloadComponentData_1j5o71zmj,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_2789477278097085_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2789477278097085_reload_qn9ec8i16],
    event: 'reload',
  });
  const act_6182083303232630_reloadComponentData_9vr3u67fx =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_6182083303232630_reload_bkcbyj1zw =
    new actionTypes.EventHandlerImpl({
      action: act_6182083303232630_reloadComponentData_9vr3u67fx,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_6182083303232630_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_6182083303232630_reload_bkcbyj1zw],
    event: 'reload',
  });
  const act_5361955607147334_reloadComponentData_vmmy4yp3c =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_5361955607147334_reload_fdzk2o4at =
    new actionTypes.EventHandlerImpl({
      action: act_5361955607147334_reloadComponentData_vmmy4yp3c,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_5361955607147334_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5361955607147334_reload_fdzk2o4at],
    event: 'reload',
  });
  const act_384677887161204_reloadComponentData_1jvulkned =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_384677887161204_reload_ec0c1oxja =
    new actionTypes.EventHandlerImpl({
      action: act_384677887161204_reloadComponentData_1jvulkned,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_384677887161204_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_384677887161204_reload_ec0c1oxja],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_close_45ku0wt3m,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_tbmkzars7,
    inst_evh_7315092382398562_reload,
    inst_evh_4615685607825208_clickPreventStop_7xdcyzqln,
    reload: inst_evh_4615685607825208_clickPreventStop_7xdcyzqln,
    inst_evh_4615685607825208_clickPreventStop,
    inst_evh_2095089031469157_success_vkocbsbe3,
    inst_evh_2095089031469157_success,
    inst_evh_2679161326024595_reload_wfmu2365x,
    inst_evh_2679161326024595_reload,
    inst_evh_7987652045662005_reload_0o3l7dbxa,
    inst_evh_7987652045662005_reload,
    inst_evh_6490121676898844_reload_44387hp8c,
    inst_evh_6490121676898844_reload,
    inst_evh_2789477278097085_reload_qn9ec8i16,
    inst_evh_2789477278097085_reload,
    inst_evh_6182083303232630_reload_bkcbyj1zw,
    inst_evh_6182083303232630_reload,
    inst_evh_5361955607147334_reload_fdzk2o4at,
    inst_evh_5361955607147334_reload,
    inst_evh_384677887161204_reload_ec0c1oxja,
    inst_evh_384677887161204_reload,
  };

  const evh_7315092382398562_close_45ku0wt3m = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_45ku0wt3m.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_tbmkzars7 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_tbmkzars7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_clickPreventStop_7xdcyzqln = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_4615685607825208_clickPreventStop_7xdcyzqln.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_4615685607825208_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_success_vkocbsbe3 = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_success_vkocbsbe3.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_success = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_success.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2679161326024595_reload_wfmu2365x = async (event, scope) => {
    return await actions.inst_evh_2679161326024595_reload_wfmu2365x.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2679161326024595_reload = async (event, scope) => {
    return await actions.inst_evh_2679161326024595_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7987652045662005_reload_0o3l7dbxa = async (event, scope) => {
    return await actions.inst_evh_7987652045662005_reload_0o3l7dbxa.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7987652045662005_reload = async (event, scope) => {
    return await actions.inst_evh_7987652045662005_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6490121676898844_reload_44387hp8c = async (event, scope) => {
    return await actions.inst_evh_6490121676898844_reload_44387hp8c.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6490121676898844_reload = async (event, scope) => {
    return await actions.inst_evh_6490121676898844_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2789477278097085_reload_qn9ec8i16 = async (event, scope) => {
    return await actions.inst_evh_2789477278097085_reload_qn9ec8i16.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2789477278097085_reload = async (event, scope) => {
    return await actions.inst_evh_2789477278097085_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6182083303232630_reload_bkcbyj1zw = async (event, scope) => {
    return await actions.inst_evh_6182083303232630_reload_bkcbyj1zw.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6182083303232630_reload = async (event, scope) => {
    return await actions.inst_evh_6182083303232630_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5361955607147334_reload_fdzk2o4at = async (event, scope) => {
    return await actions.inst_evh_5361955607147334_reload_fdzk2o4at.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5361955607147334_reload = async (event, scope) => {
    return await actions.inst_evh_5361955607147334_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_384677887161204_reload_ec0c1oxja = async (event, scope) => {
    return await actions.inst_evh_384677887161204_reload_ec0c1oxja.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_384677887161204_reload = async (event, scope) => {
    return await actions.inst_evh_384677887161204_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="Contract"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <div class="page--v2 d-flex flex-column" v-if="loaded">
      <MaterialTabs
        :tabManager="tabManager"
        :title="item.snapshot('contract_number')"
        :parentText="gettext('Contracts')"
        :parentTo="b_8564662037462133_parentTo"
        class="h-100"
      >
        <template #actions>
          <ItpButton
            variant="light"
            icon="fa-sync-alt"
            :text="gettext('Reload')"
            size="sm"
            :spinning="actions.reload.isRunning"
            @click.prevent.stop="
              evh_4615685607825208_clickPreventStop($event, {})
            "
          >
          </ItpButton>
          <BtnDeleteResource
            :resource="item"
            :dialogHeader="pgettext('Contract/DialogTitle', 'Delete contract')"
            @success="evh_2095089031469157_success($event, {})"
          >
          </BtnDeleteResource>
          <UcContractRevisionDropdown :contract="item">
          </UcContractRevisionDropdown>
        </template>
        <BTab :title="gettext('General')" class="container-lg float-start">
          <ItpRow>
            <ItpCol :w="6">
              <UcContractGeneralInfo
                :contract="item"
                :reload="lastLoaded"
                :readonly="readonly"
                class="pb-5"
                @reload="evh_2679161326024595_reload($event, {})"
              >
              </UcContractGeneralInfo>
              <UcContractServices
                :contract="item"
                :reload="lastLoaded"
                :readonly="readonly"
                class="pb-5"
                @reload="evh_7987652045662005_reload($event, {})"
              >
              </UcContractServices>
              <UcContractEvents
                :contract="item"
                :reload="lastLoaded"
                :readonly="readonly"
                class="pb-5"
                @reload="evh_6490121676898844_reload($event, {})"
              >
              </UcContractEvents>
            </ItpCol>
            <ItpCol :w="6">
              <UcContractLifecycle
                :contract="item"
                :reload="lastLoaded"
                :readonly="readonly"
                class="pb-5"
                @reload="evh_2789477278097085_reload($event, {})"
              >
              </UcContractLifecycle>
              <UcContractInvoicing
                :contract="item"
                :reload="lastLoaded"
                :readonly="readonly"
                class="pb-5"
                @reload="evh_6182083303232630_reload($event, {})"
              >
              </UcContractInvoicing>
              <UcContractTerms
                :contract="item"
                :reload="lastLoaded"
                :readonly="readonly"
                class="pb-5"
                @reload="evh_5361955607147334_reload($event, {})"
              >
              </UcContractTerms>
              <UcCommentsList
                :parentId="item.revision_id"
                :parent="item"
                :reload="lastLoaded"
                class="pb-5"
              >
              </UcCommentsList>
              <UcAttachmentsList
                :parent_id="item.revision_id"
                :parent="item"
                :reload="lastLoaded"
                :readonly="readonly"
                class="pb-5"
                @reload="evh_384677887161204_reload($event, {})"
              >
              </UcAttachmentsList>
            </ItpCol>
          </ItpRow>
        </BTab>
        <BTab
          :title="pgettext('Contract', 'Billing list')"
          class="container-fluid h-100"
        >
          <UcBillingList
            :contractId="item.id"
            :reload="lastLoaded"
            class="h-100"
            v-if="tabManager.visited(1)"
          >
          </UcBillingList>
        </BTab>
        <BTab
          :title="pgettext('Contract', 'Invoices')"
          class="container-fluid h-100"
        >
          <InvoiceLinesView
            :contractId="item.id"
            :reload="lastLoaded"
            class="h-100"
            v-if="tabManager.visited(2)"
          >
          </InvoiceLinesView>
        </BTab>
      </MaterialTabs>
    </div>
  </ItpPage>
</template>
